import { Field, Form, FormSpy, useForm } from 'react-final-form';
import styles from './BusinessUpdates.module.scss';
import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { MonthlyReportFormContext } from '../context/MonthlyReportFormContext';
import arrayMutators from 'final-form-arrays'
import { findFirstErrorField, setFieldTouchedMutator } from '../../../components/functions/utilities';
import { ApplicationContextType, MonthlyReportSteps } from '../../../interfaces/interfaces';
import moment from 'moment';
import AddDataSection from '../components/AddDataSection/AddDataSection';
import { Modal } from 'reactstrap';
import DebtScheduleGrid from '../../Underwriting/Revenue Details/DebtSchedule/DebtScheduleGrid';
import DebtorScheduleGrid from '../../Underwriting/Revenue Details/DebtorSchedule/DebtorScheduleGrid';
import MonthlyReportShareholding from './MonthlyReportShareholding/MonthlyReportShareholding';
import BusinessUdpateOtherFields from './OtherFields/BusinessUpdateOtherFields';
import { validateBusinessUpdateStep } from './businessUpdate.helper';
import { useSearchParams } from 'react-router-dom';


type Props = {
  setFormPristine: (value: boolean) => void,
}

const BusinessUpdates = forwardRef(({ setFormPristine }: Props, ref) => {

  const { formData, setStepValidity, extraFormDetails, formStepsValidity } = useContext(MonthlyReportFormContext)
  const formRef = useRef(null);
  const [isOpenDebtSchedule, setIsOpenDebtSchedule] = useState(false);
  const [isOpenDebtorSchedule, setIsOpenDebtorSchedule] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const showError = searchParams.get('error');
    if (showError == "true") {
      formRef?.current?.submit();
      scrollToFirstError();
    }
  }, [searchParams])

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      return new Promise((resolve) => {
        formRef.current.submit();
        // console.log("pristine", formRef?.current?.getState())
        if (!formStepsValidity[MonthlyReportSteps.businessUpdates]) {
          scrollToFirstError();
          resolve({ success: false, values: formRef?.current?.getState().values, pristine: formRef?.current?.getState().pristine })
        } else {
          // deleteFileKeysOnSubmit();
          resolve({ success: true, values: formRef?.current?.getState().values, pristine: formRef?.current?.getState().pristine })
        }
      })
    }
  }), [formStepsValidity]);


  const scrollToFirstError = () => {
    try {

      const formState = formRef.current.getState();
      const { errors } = formState;

      // console.log('form state', formState);
      // console.log('formState', formState)
      const firstErrorField = findFirstErrorField(errors);

      // console.log('firstErrorField', firstErrorField);
      // console.log('firstErrorField', firstErrorField);
      const errorField = document.querySelector(`[name="${firstErrorField}"]`) ?? document.getElementById(`${firstErrorField}`);
      // console.log('errorField', errorField);
      if (errorField) {
        errorField.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (error) {
      console.log('error', error)
    }
  };

  const processedFormDetails = useMemo(() => {
    const monthYear = extraFormDetails?.monthYear;
    const formattedMonthYear = moment(monthYear, "MM-YYYY");
    return {
      monthYear: formattedMonthYear,
      endDate: formattedMonthYear.endOf('month'),
    }
  }, [extraFormDetails])

  return (

    <div className={styles.rootContainer}>
      <Form
        initialValues={formData}
        mutators={{
          ...arrayMutators,
          setFieldTouched: setFieldTouchedMutator
        }}
        onSubmit={(values) => {
          return {}
        }}
        validate={(values) => {
          const { errors } = validateBusinessUpdateStep(values, processedFormDetails.monthYear.clone().format("MM-YYYY"));
          // console.log('business updates errors', errors);
          return errors;
        }}
        render={({ handleSubmit, form }) => {
          formRef.current = form;
          return (
            <form className={styles.formWrapper} onSubmit={handleSubmit}>
              <FormSpy subscription={{ valid: true, pristine: true }} onChange={(state) => {

                setFormPristine(state.pristine)
                setStepValidity(MonthlyReportSteps.businessUpdates, state.valid)
              }} />
              <div className={styles.formRootContainer}>
                <div className={styles.sectionContainer}>
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>
                      Debt Schedule
                    </div>
                  </div>
                  <div className={styles.sectionFieldsContainer}>
                    <div className={styles.fieldLabel}>
                      Update your debt as of {processedFormDetails.endDate.clone().format("MMM DD")}
                    </div>
                    <AddDataSection
                      sectionTitle='Debt Schedule'
                      onClickHandler={() => {
                        setIsOpenDebtSchedule(true)
                      }}
                      btnText='Update'
                    />

                    <Field name='debtSchedule'>
                      {(props) => {
                        return (
                          <>
                            {props?.meta?.error && props?.meta?.touched ? <div className={styles.errorText}>
                              {props?.meta?.error}
                            </div> : null}
                          </>
                        )
                      }}
                    </Field>
                  </div>
                </div>

                <div className={styles.sectionContainer}>
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>
                      Debtor Schedule
                    </div>
                  </div>
                  <div className={styles.sectionFieldsContainer}>
                    <div className={styles.fieldLabel}>
                      Update your outstanding receivables as of {processedFormDetails.endDate.clone().format("MMM DD")}
                    </div>
                    <AddDataSection
                      sectionTitle='Debtor Schedule'
                      onClickHandler={() => {
                        setIsOpenDebtorSchedule(true)
                      }}
                      btnText='Update'
                    />
                    <Field name='debtorSchedule'>
                      {(props) => {
                        return (
                          <>
                            {props?.meta?.error && props?.meta?.touched ? <div className={styles.errorText}>
                              {props?.meta?.error}
                            </div> : null}
                          </>
                        )
                      }}
                    </Field>
                  </div>
                </div>

                <div className={styles.sectionContainer}>
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>
                      Investments
                    </div>
                  </div>
                  <MonthlyReportShareholding monthYear={processedFormDetails.monthYear} />

                </div>

                <div className={styles.sectionContainer}>
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>
                      New Product Launch
                    </div>
                  </div>
                  <BusinessUdpateOtherFields
                    rootFieldName='newProductLaunch'
                    fieldType='textfieldwithchip'
                    fieldName='products'
                    fieldLabel='Product Name(s)'
                    fieldPlaceholder='Product ABC, Product DEF'
                    conditionField={true}
                    radioButtonLabel={`Did you launch any major new product in ${processedFormDetails.monthYear.clone().format("MMM")}?`}
                    radioLabels={[
                      { label: 'Yes', value: 'yes' },
                      { label: 'No', value: 'no' },
                    ]}
                    fieldErrorLabel='Enter new products'
                    monthYear={processedFormDetails.monthYear.clone().format("MM-YYYY")}
                  />
                </div>


                <div className={styles.sectionContainer}>
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>
                      New Sales Channel
                    </div>
                  </div>
                  <BusinessUdpateOtherFields
                    rootFieldName='newMarketplace'
                    fieldType='textfieldwithchip'
                    fieldName='marketplaces'
                    fieldLabel='Marketplace(s)'
                    fieldPlaceholder='FirstCry, BigBasket'
                    conditionField={true}
                    radioButtonLabel={`Did you start selling on any new marketplace or sales channel in ${processedFormDetails.monthYear.clone().format("MMM")}?`}
                    radioLabels={[
                      { label: 'Yes', value: 'yes' },
                      { label: 'No', value: 'no' },
                    ]}
                    fieldErrorLabel='Enter new sales channels'
                    monthYear={processedFormDetails.monthYear.clone().format("MM-YYYY")}
                  />
                </div>

                <div className={styles.sectionContainer}>
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>
                      Inventory Position
                    </div>
                  </div>
                  <BusinessUdpateOtherFields
                    rootFieldName='unsoldInventory'
                    fieldType='numberfield'
                    fieldName='value'
                    conditionField={false}
                    fieldPlaceholder='10,00,000'
                    fieldLabel={`Value of Inventory as on ${processedFormDetails.endDate.clone().format("MMM DD")}`}
                    fieldErrorLabel='Enter the value'
                    monthYear={processedFormDetails.monthYear.clone().format("MM-YYYY")}
                    fieldSubText='Use cost or book value, not MRP.'
                  />
                </div>

                <Modal
                  isOpen={isOpenDebtSchedule}
                  toggle={() => {
                    setIsOpenDebtSchedule(!isOpenDebtSchedule)
                  }}
                  size='lg'
                  fullscreen={true}
                  keyboard={false}
                >
                  <DebtScheduleGrid formType={ApplicationContextType.monthlyReport} fieldName='debtSchedule' setIsOpen={setIsOpenDebtSchedule} isOpen={isOpenDebtSchedule} />
                </Modal>

                <Modal
                  isOpen={isOpenDebtorSchedule}
                  toggle={() => {
                    setIsOpenDebtorSchedule(!isOpenDebtorSchedule)
                  }}
                  size='lg'
                  fullscreen={true}
                  keyboard={false}
                >

                  <DebtorScheduleGrid formType={ApplicationContextType.monthlyReport} fieldName='debtorSchedule' setIsOpen={setIsOpenDebtorSchedule} isOpen={isOpenDebtorSchedule} />
                </Modal>
              </div>
            </form>
          )
        }}
      />
    </div>
  )
});

export default BusinessUpdates;